import React, { useEffect, useState } from 'react'
import moment from 'moment'
import _cx from 'classnames/bind'
import { useApplicationContext, Icon, Text } from '@adc/ui-adc'
import type { primitives } from '@adc/blueprint'
import type { PDPDisplayedDocumentsType } from '@adc/blueprint/lib/listing/pdp/types'
import { useModalFeatures } from '../../hooks/useModalFeatures'
import PopupTooltip from '../../components/popupTooltip'
import cls from './index.scss'

const cx = _cx.bind(cls)

export type MobileDocumentListComponentProps = {
  documents: PDPDisplayedDocumentsType[]
  elmId: string
  viewDocument: (document: PDPDisplayedDocumentsType) => void
}
type MobileViewDocumentType = {
  disabled?: boolean
  disabledTooltip?: string
  embeddedContext?: string
  label: string
  updatedDate?: string | number | Date | null
  elmId: string
  onClick: () => void
}
export const MobileViewDocument = ({
  disabled,
  disabledTooltip = '',
  embeddedContext = 'browser',
  label,
  updatedDate,
  elmId,
  onClick,
}: MobileViewDocumentType) => {
  const DocumentRow = (
    <div className={cx('mobile-document-row')}>
      <div className={cx('mobile-document-image')}>
        <Icon
          name='file-pdf'
          iconStyle='regular'
          color={disabled ? 'grayMedium' : 'gray'}
          size='big'
        />
      </div>
      <div>
        <Text size='tiny' color={disabled ? 'grayMedium' : 'info'}>
          {label}
        </Text>
        {!disabled &&
          updatedDate !== undefined &&
          moment(updatedDate).isValid() && (
            <Text size='tiny' color='gray'>
              Added: {moment(updatedDate).format('MMM DD, YYYY')}
            </Text>
          )}
      </div>
    </div>
  )

  return !disabled ? (
    <a
      className='u-display-f u-display-f-vcenter'
      onClick={onClick}
      target={embeddedContext === 'webview' ? '_self' : '_blank'}
      rel='noopener noreferrer'
      data-elm-id={`${elmId}_doc`}
      title={label}
    >
      {DocumentRow}
    </a>
  ) : (
    <PopupTooltip
      content={disabledTooltip}
      position='top left'
      elmId='file_document'
      trigger={DocumentRow}
    />
  )
}
type MobileDocumentListComponentType = {
  documents: PDPDisplayedDocumentsType[]
  elmId: string
  viewDocument: (document: PDPDisplayedDocumentsType, context?: string) => void
}

const MobileDocumentListComponent: React.FC<
  MobileDocumentListComponentProps
> = ({ documents, elmId, viewDocument }: MobileDocumentListComponentType) => {
  const { pushOnboardingQueueItem, toggleAuthModal } = useModalFeatures()
  const [selectedDocument, setSelectedDocument] =
    useState<PDPDisplayedDocumentsType | null>(null)
  const applicationContext = useApplicationContext()
  const untypedUser = applicationContext.user
  const user = untypedUser as primitives.AuctionUser

  const handleDocumentSelected = (document: PDPDisplayedDocumentsType) => {
    if (!user.logged_in) {
      pushOnboardingQueueItem([handleDocumentSelected, [document]])
      toggleAuthModal('login')
    }
    setSelectedDocument(document)
  }

  useEffect(() => {
    if (user.logged_in && selectedDocument) {
      Promise.resolve().then(() =>
        viewDocument(selectedDocument, applicationContext.embeddedContext)
      )
      setSelectedDocument(null)
    }
  }, [user.logged_in, selectedDocument, viewDocument])

  return (
    <div className={cx('document-list-mobile')}>
      {documents.map((document: PDPDisplayedDocumentsType, index: number) => (
        <React.Fragment key={`document_${index}`}>
          <MobileViewDocument
            elmId={elmId}
            embeddedContext={applicationContext.embeddedContext}
            label={document.label}
            updatedDate={document.updatedDate}
            onClick={() => handleDocumentSelected(document)}
          />
        </React.Fragment>
      ))}
    </div>
  )
}

export default MobileDocumentListComponent
